import { getDebugMode, createLogger } from "../helpers/logger.js";

export function findCleonContainers() {
  return Array.from(
    document.querySelectorAll(
      "[data-cleon-zone], #video-container[data], #video-container[data-player]"
    )
  );
}

export function buildPlayerDom(container, playerId) {
  container.innerHTML = `
    <div class="video-wrapper-fluid">
      <div class="player-container player-fluid">
        <video
          id="${playerId}"
          class="video-js vjs-default-skin"
          playsinline
          webkit-playsinline
          preload="metadata">
        </video>
      </div>
    </div>
  `;
}

export function moveContainerToParagraph(container, index) {
  const debug = getDebugMode(container);
  const log = createLogger("ContainerDom", debug);

  const rawIndex = index;
  const paragraphIndex =
    typeof index === "number"
      ? index
      : Number.parseInt(String(index || "").trim(), 10);

  return new Promise((resolve) => {
    let settled = false;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    function getText(el) {
      return (el?.textContent || "").replace(/\s+/g, " ").trim();
    }

    function ensureAnchor(target, doc = document) {
      let inlineAnchor =
        target?.previousElementSibling?.classList?.contains("cleon-inline-anchor")
          ? target.previousElementSibling
          : null;

      if (!inlineAnchor && target?.parentNode) {
        inlineAnchor = doc.createElement("div");
        inlineAnchor.className = "cleon-inline-anchor";
        inlineAnchor.setAttribute("data-cleon-inline-anchor", "1");
        inlineAnchor.style.cssText = [
          "display:block",
          "width:0",
          "height:0",
          "min-height:0",
          "max-height:0",
          "margin:0",
          "padding:0",
          "overflow:hidden",
          "visibility:hidden",
          "pointer-events:none",
          "clear:both",
        ].join(";");

        target.parentNode.insertBefore(inlineAnchor, target);
      }

      return inlineAnchor;
    }

    function isBadContainer(el) {
      return !!el?.closest?.(
        [
          "header",
          "footer",
          "nav",
          "aside",
          "form",
          "button",
          "script",
          "style",
          "noscript",
          "iframe",

          ".sidebar",
          ".widget",
          ".menu",
          ".nav",
          ".footer",
          ".header",
          ".comments",
          ".comment",
          ".related",
          ".recommended",
          ".share",
          ".social",
          ".breadcrumb",

          ".advertisement",
          ".advertisements",
          ".ad",
          ".ads",
          ".adslot",
          ".ad-slot",
          ".banner",
          ".dfp",
          ".gpt",
          ".googletag",
          ".GoogleActiveViewElement",

          "[class*='sidebar']",
          "[class*='widget']",
          "[class*='comment']",
          "[class*='related']",
          "[class*='share']",
          "[class*='social']",
          "[class*='advert']",
        ].join(",")
      );
    }

    function isMeaningfulParagraph(p, doc = document) {
      if (!p || isBadContainer(p)) return false;

      const text = getText(p);
      if (text.length < 40) return false;

      const rect = p.getBoundingClientRect?.();
      if (!rect || rect.width <= 0 || rect.height <= 0) return false;

      const style = doc.defaultView?.getComputedStyle?.(p);
      if (style && (style.display === "none" || style.visibility === "hidden")) {
        return false;
      }

      const links = Array.from(p.querySelectorAll("a"));
      const linkTextLength = links.reduce(
        (sum, a) => sum + getText(a).length,
        0
      );

      if (links.length >= 3 && linkTextLength > text.length * 0.6) {
        return false;
      }

      return true;
    }

    function getContentRoot(doc = document) {
      const selectors = [
        "article",
        "[itemprop='articleBody']",
        ".article-content",
        ".article-body",
        ".entry-content",
        ".post-content",
        ".post-body",
        ".story-content",
        ".single-post-content",
        ".td-post-content",
        ".tdb-block-inner",
        "main",
        "[role='main']",
        ".content-area",
        ".main-content",
        "body",
      ];

      const candidates = Array.from(
        new Set(doc.querySelectorAll(selectors.join(",")))
      ).filter((el) => {
        if (!el) return false;
        if (isBadContainer(el)) return false;
        return true;
      });

      let best = doc.body;
      let bestScore = -Infinity;

      for (const el of candidates) {
        const paragraphs = Array.from(el.querySelectorAll("p")).filter((p) =>
          isMeaningfulParagraph(p, doc)
        );

        if (!paragraphs.length) continue;

        const textLength = paragraphs.reduce(
          (sum, p) => sum + getText(p).length,
          0
        );

        const tagName = el.tagName?.toLowerCase() || "";
        const identity = `${tagName} ${el.id || ""} ${
          typeof el.className === "string" ? el.className : ""
        }`.toLowerCase();

        let score = paragraphs.length * 1000 + Math.min(textLength, 12000);

        if (tagName === "article") score += 5000;
        if (identity.includes("article")) score += 2500;
        if (identity.includes("entry-content")) score += 2500;
        if (identity.includes("post-content")) score += 2200;
        if (identity.includes("article-content")) score += 2200;
        if (identity.includes("article-body")) score += 2200;
        if (identity.includes("story-content")) score += 1800;
        if (identity.includes("td-post-content")) score += 1800;
        if (el.matches?.("[itemprop='articleBody']")) score += 5000;
        if (tagName === "main") score -= 500;
        if (el === doc.body) score -= 10000;

        if (score > bestScore) {
          best = el;
          bestScore = score;
        }
      }

      log.debug("📄 Content root selected", {
        tag: best?.tagName || null,
        id: best?.id || null,
        className: typeof best?.className === "string" ? best.className : null,
        score: bestScore,
        docIsParent: doc !== document,
      });

      return best || doc.body;
    }

    function findTargetParagraph(targetIndex, doc = document) {
      const root = getContentRoot(doc);

      const paragraphs = Array.from(root.querySelectorAll("p")).filter((p) =>
        isMeaningfulParagraph(p, doc)
      );

      if (!paragraphs.length) {
        log.debug("📄 No usable article paragraphs found", {
          docIsParent: doc !== document,
          rootTag: root?.tagName || null,
          rootId: root?.id || null,
          rootClass: typeof root?.className === "string" ? root.className : null,
        });

        return null;
      }

      const safeIndex = Math.max(1, targetIndex);
      const target =
        paragraphs[safeIndex - 1] || paragraphs[paragraphs.length - 1];

      log.debug("📄 Target paragraph resolved", {
        requested: targetIndex,
        found: paragraphs.length,
        selected: paragraphs.indexOf(target) + 1,
        docIsParent: doc !== document,
        preview: getText(target).slice(0, 120),
      });

      return target;
    }

    function createVisualSpacer(parentDoc, targetParagraph, iframeEl, height) {
      const zoneHint =
        iframeEl?.id ||
        iframeEl?.getAttribute?.("name") ||
        iframeEl?.getAttribute?.("data-cleon-zone") ||
        "default";

      const selector = `[data-cleon-visual-spacer="1"][data-cleon-zone-hint="${CSS.escape(
        zoneHint
      )}"]`;

      let spacer = parentDoc.querySelector(selector);

      if (!spacer) {
        spacer = parentDoc.createElement("div");
        spacer.setAttribute("data-cleon-visual-spacer", "1");
        spacer.setAttribute("data-cleon-zone-hint", zoneHint);
        targetParagraph.after(spacer);
      }

      spacer.style.cssText = [
        "display:block",
        "width:100%",
        "max-width:640px",
        `height:${height}px`,
        `min-height:${height}px`,
        `max-height:${height}px`,
        "margin:24px auto",
        "padding:0",
        "clear:both",
        "position:relative",
        "overflow:hidden",
        "background:transparent",
        "pointer-events:none",
        "box-sizing:border-box",
      ].join(";");

      return spacer;
    }

    function collapseOriginalIframeSpace(iframeEl) {
      const parent = iframeEl?.parentElement;
      if (!parent?.style) return;

      const className = String(parent.className || "");
      const id = String(parent.id || "");

      // Do not display:none the parent, because that can hide/trap the iframe.
      // Just remove its layout footprint while allowing the fixed iframe to escape.
      parent.style.setProperty("height", "0px", "important");
      parent.style.setProperty("min-height", "0px", "important");
      parent.style.setProperty("max-height", "0px", "important");
      parent.style.setProperty("margin", "0px", "important");
      parent.style.setProperty("padding", "0px", "important");
      parent.style.setProperty("overflow", "visible", "important");

      log.debug("🧹 Collapsed original iframe parent space", {
        parentTag: parent.tagName,
        parentId: id || null,
        parentClass: className || null,
      });
    }

    function bindIframeVisualArticlePlacement(iframeEl, targetParagraph) {
      if (!iframeEl || !targetParagraph) return false;

      const parentDoc = iframeEl.ownerDocument;
      const parentWin = parentDoc.defaultView;
      if (!parentWin) return false;

      const initialParagraphRect = targetParagraph.getBoundingClientRect();
      const width = Math.min(
        640,
        Math.max(300, Math.round(initialParagraphRect.width || 640))
      );
      const height = Math.round(width * 9 / 16);

      const spacer = createVisualSpacer(parentDoc, targetParagraph, iframeEl, height);

      let stickyMode = false;
      let rafId = null;

      function applyArticlePosition() {
        if (stickyMode) return;
        if (!iframeEl.isConnected || !spacer.isConnected) return;

        const rect = spacer.getBoundingClientRect();

        iframeEl.setAttribute("data-cleon-visual-placed", "1");

        iframeEl.style.setProperty("position", "fixed", "important");
        iframeEl.style.setProperty("display", "block", "important");
        iframeEl.style.setProperty("left", `${Math.round(rect.left)}px`, "important");
        iframeEl.style.setProperty("top", `${Math.round(rect.top)}px`, "important");
        iframeEl.style.setProperty("right", "auto", "important");
        iframeEl.style.setProperty("bottom", "auto", "important");

        iframeEl.style.setProperty("width", `${Math.round(rect.width)}px`, "important");
        iframeEl.style.setProperty("max-width", `${Math.round(rect.width)}px`, "important");
        iframeEl.style.setProperty("min-width", "0", "important");

        iframeEl.style.setProperty("height", `${Math.round(rect.height)}px`, "important");
        iframeEl.style.setProperty("min-height", `${Math.round(rect.height)}px`, "important");
        iframeEl.style.setProperty("max-height", `${Math.round(rect.height)}px`, "important");

        iframeEl.style.setProperty("margin", "0", "important");
        iframeEl.style.setProperty("padding", "0", "important");
        iframeEl.style.setProperty("border", "0", "important");
        iframeEl.style.setProperty("overflow", "hidden", "important");
        iframeEl.style.setProperty("z-index", "10", "important");
        iframeEl.style.setProperty("box-sizing", "border-box", "important");
      }

      function scheduleArticlePosition() {
        if (rafId) return;

        rafId = parentWin.requestAnimationFrame(() => {
          rafId = null;
          applyArticlePosition();
        });
      }

      const controller = {
        spacer,
        setSticky(value) {
          stickyMode = !!value;
        },
        applyArticlePosition,
        restoreArticlePosition() {
          stickyMode = false;
          applyArticlePosition();
        },
        cleanup() {
          parentWin.removeEventListener("scroll", scheduleArticlePosition);
          parentWin.removeEventListener("resize", scheduleArticlePosition);

          if (rafId) {
            parentWin.cancelAnimationFrame(rafId);
            rafId = null;
          }
        },
      };

      try {
        if (iframeEl.__cleonVisualPlacement?.cleanup) {
          iframeEl.__cleonVisualPlacement.cleanup();
        }
      } catch {}

      iframeEl.__cleonVisualPlacement = controller;

      parentWin.addEventListener("scroll", scheduleArticlePosition, { passive: true });
      parentWin.addEventListener("resize", scheduleArticlePosition);

      collapseOriginalIframeSpace(iframeEl);
      applyArticlePosition();

      log.debug("✅ Friendly iframe visually placed over article spacer", {
        spacerRect: spacer.getBoundingClientRect?.(),
        iframeId: iframeEl.id || null,
        preview: getText(targetParagraph).slice(0, 120),
      });

      return true;
    }

    if (!Number.isInteger(paragraphIndex) || paragraphIndex <= 0) {
      if (window.self !== window.top) {
        try {
          const iframeEl = window.frameElement;
          const parentDoc = iframeEl?.ownerDocument;
          if (iframeEl && parentDoc) ensureAnchor(iframeEl, parentDoc);
        } catch {}
      } else {
        ensureAnchor(container, document);
      }

      log.debug("⏭ Player stays in original position", {
        rawIndex,
        parsedIndex: paragraphIndex,
      });

      finish(false);
      return;
    }

    // Friendly iframe mode:
    // Do not parent-spawn.
    // Do not move iframe DOM.
    // Create a spacer in the article and visually position the iframe over it.
    if (window.self !== window.top) {
      try {
        const iframeEl = window.frameElement;
        const parentDoc = iframeEl?.ownerDocument;

        if (!iframeEl || !parentDoc?.body) {
          log.debug("⏭ Inside iframe, parent unavailable");
          finish(false);
          return;
        }

        const targetParagraph = findTargetParagraph(paragraphIndex, parentDoc);

        if (!targetParagraph) {
          ensureAnchor(iframeEl, parentDoc);
          log.debug("⏭ Inside iframe, no parent paragraph found");
          finish(false);
          return;
        }

        const placed = bindIframeVisualArticlePlacement(iframeEl, targetParagraph);

        finish(placed);
        return;
      } catch (err) {
        log.debug("⏭ Inside iframe visual placement failed", {
          error: err?.message || String(err),
        });

        finish(false);
        return;
      }
    }

    function tryMove() {
      const targetParagraph = findTargetParagraph(paragraphIndex, document);
      if (!targetParagraph) return false;

      const inlineAnchor = ensureAnchor(container, document);
      if (!inlineAnchor) return false;

      targetParagraph.after(inlineAnchor);
      inlineAnchor.after(container);

      log.debug("✅ Player moved after paragraph", {
        paragraphIndex,
      });

      finish(true);
      return true;
    }

    if (tryMove()) return;

    let moveQueued = false;

    const observer = new MutationObserver(() => {
      if (moveQueued || settled) return;

      moveQueued = true;

      requestAnimationFrame(() => {
        moveQueued = false;

        if (settled) {
          observer.disconnect();
          return;
        }

        if (tryMove()) {
          observer.disconnect();
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    setTimeout(() => {
      observer.disconnect();

      log.debug("⏱ Paragraph move timed out", {
        rawIndex,
        paragraphIndex,
      });

      finish(false);
    }, 3000);
  });
}

export function hidePending(container) {
  container.classList.add("ad-slot--pending");
}

export function showPending(container) {
  container.classList.remove("ad-slot--pending");
}